/**
 * @fileoverview Heterogeneous picker-item model for the unified inbox URL-assignment field.
 * Conveys "grouping" via ordering + a per-item kind discriminator (tag / variant / site-wide),
 * since the shared LookupConfirmField renders a flat list with no native section headers.
 * @module features/sku-alias/components/urlPickerItems
 */

import type { LookupConfirmItem } from '@/shared/components/LookupConfirmField'
import type { Tag } from '@/features/tags'
import type { Variant } from '@/lib/variantService'

/**
 * A single suggestion in the unified inbox picker. `id` is a unique string across
 * kinds so it satisfies {@link LookupConfirmItem} and keys cleanly in the list.
 */
export type PickerItem = LookupConfirmItem &
  (
    | { kind: 'tag'; id: string; tag: Tag }
    | { kind: 'variant'; id: string; variant: Variant }
    | { kind: 'general'; id: 'general' }
  )

/** Builds the stable id for a tag picker item. */
export function tagItemId(tag: Pick<Tag, 'uuid'>): string {
  return `tag:${tag.uuid}`
}

/** Builds the stable id for a variant picker item. */
export function variantItemId(variant: Pick<Variant, 'id'>): string {
  return `variant:${variant.id}`
}

/** Wraps a variant as a picker item (used for AI-suggested pre-selection). */
export function toVariantPickerItem(variant: Variant): PickerItem {
  return { kind: 'variant', id: variantItemId(variant), variant }
}

/** A `/collections/*` canonical path is the signal to surface tags first. */
export function isCollectionPath(path: string): boolean {
  return path.split('?')[0]!.startsWith('/collections/')
}

/**
 * Lenient tag matcher: a tag matches when ANY whitespace-delimited token of the
 * query appears in its name or slug. An empty query matches every tag.
 *
 * This mirrors how a user actually narrows a short tag list — e.g. typing
 * "nail dust" surfaces "Dust collectors" via the "dust" token. The previous
 * full-phrase `includes(query)` check dropped such tags because the entire
 * phrase was not a contiguous substring of the tag name.
 */
export function tagMatchesQuery(tag: Pick<Tag, 'name' | 'slug'>, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true
  const haystack = `${tag.name} ${tag.slug}`.toLowerCase()
  return normalizedQuery
    .split(/\s+/)
    .filter(Boolean)
    .some(token => haystack.includes(token))
}

export interface BuildPickerItemsOptions {
  /** Tags available for assignment (full list; filtered client-side by query). */
  tags: Tag[]
  /** Variants already filtered/searched by the caller. */
  variants: Variant[]
  /** Canonical path of the URL being assigned; decides tag-vs-variant ordering. */
  canonicalPath: string
  /** Whether to offer the site-wide (general) option as a trailing item. */
  allowGeneral: boolean
}

/**
 * Builds the ordered union list for the picker.
 *
 * - For `/collections/*` paths, tags come first (the deliberate surfacing rule).
 * - Otherwise variants come first.
 * - Tags are filtered by a lenient, token-based match (see {@link tagMatchesQuery}).
 * - The site-wide option (if allowed) is always last.
 */
export function buildPickerItems(query: string, opts: BuildPickerItemsOptions): PickerItem[] {
  const normalizedQuery = query.trim().toLowerCase()

  const tagItems: PickerItem[] = opts.tags
    .filter(tag => tagMatchesQuery(tag, normalizedQuery))
    .map(tag => ({ kind: 'tag', id: tagItemId(tag), tag }))

  const variantItems: PickerItem[] = opts.variants.map(toVariantPickerItem)

  const generalItems: PickerItem[] = opts.allowGeneral ? [{ kind: 'general', id: 'general' }] : []

  return isCollectionPath(opts.canonicalPath)
    ? [...tagItems, ...variantItems, ...generalItems]
    : [...variantItems, ...tagItems, ...generalItems]
}
